import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/common";
import { Attribution, QuoteMark } from "@/components/testimonials/parts";
import { useFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

const span = (i, n) => (n === 1 ? "md:col-span-12" : i === 0 ? "md:col-span-7 md:row-span-2" : i < 3 ? "md:col-span-5" : "md:col-span-6");
const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const Slide = ({ t, i, n }) => {
  const lead = i === 0;
  return (
    <figure
      role="group"
      aria-roledescription="slide"
      aria-label={`${i + 1} of ${n}`}
      data-testid={`kind-words-item-${t.id}`}
      className={cn("flex w-full shrink-0 snap-center flex-col justify-between gap-10 border-line/15 py-2 md:w-auto md:py-0", span(i, n), lead ? "md:border-r md:pr-12 lg:pr-16" : "md:border-b md:pb-10 md:last:border-b-0")}
    >
      <blockquote>
        <QuoteMark className={lead ? "text-[7rem] md:text-[10rem]" : "text-[5rem] md:text-[6rem]"} />
        <p className={cn("font-display leading-[1.12] text-fg", lead ? "text-3xl md:text-[2.6rem] lg:text-5xl" : "text-3xl md:text-2xl lg:text-[1.75rem]")} data-testid={`kind-words-quote-${t.id}`}>{t.quote}</p>
      </blockquote>
      <Attribution t={t} size={lead ? "h-16 w-16" : "h-12 w-12"} />
    </figure>
  );
};

export const KindWords = () => {
  const { data } = useFetch("/testimonials?featured=true");
  const track = useRef(null);
  const [active, setActive] = useState(0);
  const items = data || [];
  if (!items.length) return null;
  const n = items.length;
  const go = (i) => {
    const el = track.current;
    const next = (i + n) % n;
    el?.scrollTo({ left: next * el.clientWidth, behavior: reduced() ? "auto" : "smooth" });
    setActive(next);
  };
  const onScroll = (e) => setActive(Math.round(e.currentTarget.scrollLeft / Math.max(1, e.currentTarget.clientWidth)));

  return (
    <section className="inverse relative overflow-hidden py-24 md:py-36" data-testid="kind-words-section" aria-labelledby="kind-words-title">
      <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-turq/10 blur-3xl" aria-hidden />
      <div className="container-x relative">
        <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionLabel number="—">Kind Words</SectionLabel>
            <h2 id="kind-words-title" className="mt-6 max-w-3xl font-display text-4xl leading-[1] sm:text-5xl lg:text-6xl">What it's like to work together.</h2>
          </div>
          {n > 1 && (
            <div className="flex items-center gap-3 md:hidden">
              <button type="button" onClick={() => go(active - 1)} aria-label="Previous testimonial" data-testid="kind-words-prev" className="grid h-11 w-11 place-items-center rounded-full border border-line/25 hover:border-turq hover:text-turq"><ChevronLeft className="h-5 w-5" /></button>
              <button type="button" onClick={() => go(active + 1)} aria-label="Next testimonial" data-testid="kind-words-next" className="grid h-11 w-11 place-items-center rounded-full border border-line/25 hover:border-turq hover:text-turq"><ChevronRight className="h-5 w-5" /></button>
            </div>
          )}
        </div>
        <Reveal>
          <div
            ref={track}
            onScroll={onScroll}
            role="region"
            aria-roledescription="carousel"
            aria-label="Testimonials"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "ArrowRight" ? go(active + 1) : e.key === "ArrowLeft" ? go(active - 1) : null)}
            data-testid="kind-words-track"
            className="no-scrollbar flex snap-x snap-mandatory gap-0 overflow-x-auto focus-visible:outline-none md:grid md:snap-none md:grid-cols-12 md:gap-x-12 md:gap-y-10 md:overflow-visible lg:gap-x-16"
          >
            {items.map((t, i) => <Slide key={t.id} t={t} i={i} n={n} />)}
          </div>
        </Reveal>
        {n > 1 && (
          <div className="mt-10 flex items-center gap-2 md:hidden" data-testid="kind-words-dots">
            {items.map((t, i) => (
              <button key={t.id} type="button" onClick={() => go(i)} aria-label={`Show testimonial ${i + 1}`} aria-current={i === active} className={cn("h-1.5 rounded-full transition-[width,background-color] duration-300", i === active ? "w-8 bg-gold" : "w-3 bg-line/30")} />
            ))}
            <span className="sr-only" aria-live="polite">Testimonial {active + 1} of {n}</span>
          </div>
        )}
      </div>
    </section>
  );
};
